import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DeleteResourceModal from "../DeleteResourceModal";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../Auth/useAuth";
import { axiosInstance } from "../axiosFetchers";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import CropYearStatusBadge from "../CropYearStatusBadge";
import CropYearCompletionIndicator from "../CropYearCompletionIndicator.jsx";

function TableRow({
  canWrite = true,
  row,
  fieldCropYears,
  setFieldCropYears,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { farmId, fieldId } = useParams();
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  function transformString(inputString) {
    if (!inputString) {
      return "Not set";
    }

    // Split the input string on the "_" character
    let tokens = inputString.split("_");

    // Capitalize each token
    let capitalizedTokens = tokens.map(
      (token) => token.charAt(0) + token.slice(1).toLowerCase()
    );

    // Join the tokens back on the space character
    let result = capitalizedTokens.join(" ");

    return result;
  }

  async function handleDeleteCropYear() {
    await axiosInstance.delete(
      `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}/cropyears/${row.cropyearId}`,
      {
        headers: {
          token: "Bearer " + user.token,
          provider: user.provider,
          "Content-Type": "application/json",
        },
      }
    );
    let filteredCropYears = fieldCropYears.filter(
      (cropYear) => cropYear.id !== row.cropyearId
    );

    setFieldCropYears(filteredCropYears);
  }

  function renderYield() {
    if (row.yield_value === null || row.yield_value === undefined) {
      return "Not set";
    }

    return row.yield_units
      ? `${row.yield_value} ${row.yield_units}`
      : `${row.yield_value}`;
  }

  return (
    <div className="grid grid-cols-8 text-sm text-[#666] hover:bg-[#f9f9f9] font-medium gap-4 border-b-[1px] border-[#DDD]">
      <div className="col-span-1 underline py-1 px-3 h-[45px] flex items-center">
        <Tooltip title="View Crop Year">
          <IconButton
            onClick={() =>
              navigate(
                `/farm/${farmId}/field/${fieldId}/cropyear/${row.cropyearId}/rotation`
              )
            }
            sx={{ paddingRight: 0 }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Analytics">
          <span>
            <IconButton
              disabled={
                (!row.analysisId && !row.can_run_analysis) ||
                (!row.analysisId && !canWrite)
              }
              onClick={() => {
                if (row.analysisId === false)
                  navigate(
                    `/farm/${farmId}/field/${fieldId}/cropyear/${row.cropyearId}/addanalysis`
                  );
                else
                  navigate(
                    `/farm/${farmId}/field/${fieldId}/cropyear/${row.cropyearId}/analysis`
                  );
              }}
              sx={{
                paddingRight: 0,
                color: row.analysisId !== false ? "green" : "",
              }}
            >
              <EqualizerIcon />
            </IconButton>
          </span>
        </Tooltip>

        {canWrite ? (
          <Tooltip title="Edit Crop Year">
            <IconButton
              onClick={() =>
                navigate(
                  `/farm/${farmId}/field/${fieldId}/cropyear/${row.cropyearId}/rotation`
                )
              }
              sx={{ paddingRight: 0 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        ) : null}

        {canWrite ? (
          <Tooltip title="Delete">
            <IconButton onClick={() => setIsWarningModalOpen(true)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : null}
        <DeleteResourceModal
          title={"Delete Crop Year"}
          description={`Are you sure you wish to permanently delete ${row.crop_year} ${row.crop_this_year}?
        
        \nPlease note that deleting your farm deletes all associated fields and crop years and all associated data. This information cannot be recovered. If any crop years are associated with Projects, those associations will also be deleted.

        Are you sure you wish to permanently delete this farm and all associated data?
        `}
          open={isWarningModalOpen}
          handleClose={setIsWarningModalOpen}
          handleDelete={handleDeleteCropYear}
        />
      </div>

      <div className="col-span-1 py-1 px-3 h-[45px] flex items-center">
        {row.crop_year ?? "Not set"}
      </div>
      <div className="col-span-1 py-1 px-3 h-[45px] flex items-center">
        {transformString(row.crop_this_year)}
      </div>
      <div className="col-span-1 py-1 px-3 h-[45px] flex items-center">
        {row.crop_frequency || "Not set"}
      </div>
      <div className="col-span-1 py-1 px-3 h-[45px] flex items-center">
        {renderYield()}
      </div>
      <div className="col-span-1 py-1 px-3 h-[45px] flex items-center">
        {transformString(row.previous_crop)}
      </div>
      <div className="col-span-1 py-2 px-3 min-h-[58px] flex items-center">
        <div className="w-full min-w-[120px]">
          <CropYearCompletionIndicator compact={true} cropYear={row} />
        </div>
      </div>
      <div className="col-span-1 py-1 px-3 h-[45px] flex items-center">
        <CropYearStatusBadge cropYear={row} />
      </div>
    </div>
  );
}

export default function CropYearTable({
  canWrite = true,
  fieldCropYears,
  setFieldCropYears,
  isLoading,
}) {
  const noDataDefaultRow =
    isLoading === false && fieldCropYears.length === 0 ? (
      <p className="my-4">
        There are no crop years to display. Please try adding a new crop year.{" "}
      </p>
    ) : null;

  return (
    <div
      style={{ border: "0.5px solid #DDD" }}
      className="overflow-hidden rounded-md shadow-md mb-4 min-w-[980px]"
    >
      {/*  Header */}
      <div className="grid grid-cols-8 text-xs bg-[#EBEBEB] text-[#333] font-bold gap-4">
        <div className="col-span-1 py-1 px-3 h-10 flex items-center">
          Actions
        </div>
        <div className="col-span-1 py-1 px-3 h-10 flex items-center">Year</div>
        <div className="col-span-1 py-1 px-3 h-10 flex items-center">Crop</div>
        <div className="col-span-1 py-1 px-3 h-10 flex items-center">
          Frequency
        </div>
        <div className="col-span-1 py-1 px-3 h-10 flex items-center">Yield</div>

        <div className="col-span-1 py-1 px-3 h-10 flex items-center">
          Previous Crop
        </div>
        <div className="col-span-1 py-1 px-3 h-10 flex items-center">
          Completion
        </div>
        <div className="col-span-1 py-1 px-3 h-10 flex items-center">
          Status
        </div>
      </div>
      {isLoading && <LinearProgress />}
      {/* Body */}
      <ul className="w-full ">
        {fieldCropYears.map((cropyear) => {
          if (typeof cropyear !== "object") {
            return null;
          }

          return (
            <li className="w-full" key={cropyear.id}>
              <TableRow
                canWrite={canWrite}
                row={{
                  ...cropyear,
                  cropyearId: cropyear.id,
                  analysisId: cropyear.analysis_id || false,
                }}
                fieldCropYears={fieldCropYears}
                setFieldCropYears={setFieldCropYears}
              />
            </li>
          );
        })}
        <li className="w-full text-center">{noDataDefaultRow}</li>
      </ul>
    </div>
  );
}
